import React, { useState, useEffect } from 'react';
import UserAvatar from './UserAvatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { findHomonymGroups } from './avatarUtils';

const ROLES = [
  { value: 'viewer',  label: 'Visualizar',  description: 'Pode apenas ver o quadro e suas tarefas', color: 'bg-gray-100 text-gray-700' },
  { value: 'member',  label: 'Editar',       description: 'Pode criar e editar tarefas',             color: 'bg-blue-100 text-blue-700' },
  { value: 'admin',   label: 'Gerenciar',    description: 'Pode editar quadro e gerenciar membros',   color: 'bg-purple-100 text-purple-700' },
  { value: 'owner',   label: 'Proprietário', description: 'Controle total do quadro',                 color: 'bg-yellow-100 text-yellow-700' },
];

const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[0];

export default function ModernBoardMembers({ board, api, user, onDataChange }) {
  const [allUsers, setAllUsers]         = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteRole, setInviteRole]     = useState('member');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [changingRole, setChangingRole] = useState({});
  const [canManageMembers, setCanManageMembers] = useState(false);
  // Identificação de homônimos: { [user_id]: texto }, preenchido quando há nomes iguais no convite
  const [identifyGroups, setIdentifyGroups] = useState(null);
  const [labels, setLabels]             = useState({});
  const [editingLabel, setEditingLabel] = useState(null); // { userId, value }

  useEffect(() => {
    fetchBoardMembers();
  }, [board]);

  const fetchBoardMembers = async () => {
    try {
      const membersRes = await api.get(`/projects/boards/${board.id}/members`);
      const enriched = (membersRes.data || []).map(m => ({
        ...m,
        name:  m.user_name || m.name || 'Usuário',
        email: m.user_email || m.email || '',
      }));
      setBoardMembers(enriched);

      const currentMember = enriched.find(m => m.user_id === user?.id && m.status === 'active');
      const memberPermissions = currentMember?.permissions || [];
      const isSystemAdmin = user?.role === 'admin' || user?.permissions?.includes('manage_system');
      const mayManage = Boolean(
        isSystemAdmin ||
        ['owner', 'admin'].includes(currentMember?.role) ||
        memberPermissions.includes('all') ||
        memberPermissions.includes('manage_members')
      );
      setCanManageMembers(mayManage);

      if (mayManage) {
        try {
          const usersRes = await api.get(`/projects/boards/${board.id}/available-members`);
          setAllUsers(usersRes.data || []);
        } catch (e) {
          console.error('Error fetching available board members:', e);
          setAllUsers([]);
        }
      } else {
        setAllUsers([]);
      }
    } catch (e) {
      console.error('Error fetching board members:', e);
      setBoardMembers([]);
      setAllUsers([]);
      setCanManageMembers(false);
    }
  };

  const effectiveName = (name, label) => String(label || name || '').trim().split(/\s+/).join(' ').toLowerCase();

  // Grupos de homônimos (membros atuais + selecionados) que envolvem alguém do convite
  const findInviteHomonyms = () => {
    const people = [
      ...boardMembers.map(m => ({ id: m.user_id, name: m.display_label || m.name, current: true })),
      ...selectedUsers.map(uid => ({ id: uid, name: allUsers.find(x => x.id === uid)?.name || '', current: false })),
    ];
    return findHomonymGroups(people).filter(g => g.some(p => !p.current));
  };

  const startInvite = () => {
    if (selectedUsers.length === 0) { toast.error('Selecione pelo menos um usuário'); return; }
    const groups = findInviteHomonyms();
    if (groups.length === 0) { handleInviteMembers({}); return; }
    setLabels({});
    setIdentifyGroups(groups);
  };

  const confirmIdentification = () => {
    const people = identifyGroups.flat();
    if (people.some(p => !String(labels[p.id] || '').trim())) { toast.error('Identifique cada pessoa com nome igual'); return; }
    const names = people.map(p => effectiveName(p.name, labels[p.id]));
    if (new Set(names).size !== names.length) { toast.error('Cada identificação precisa ser diferente das outras'); return; }
    handleInviteMembers(Object.fromEntries(people.map(p => [p.id, labels[p.id].trim()])));
  };

  const saveLabel = async () => {
    if (!editingLabel) return;
    try {
      await api.put(`/projects/boards/${board.id}/members/${editingLabel.userId}/label`, { label: editingLabel.value });
      toast.success('Identificação atualizada');
      setEditingLabel(null);
      fetchBoardMembers();
      onDataChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao salvar identificação'); }
  };

  const handleInviteMembers = async (labelsToSend = {}) => {
    setLoading(true);
    try {
      await api.post(`/projects/boards/${board.id}/members/bulk`, {
        user_ids: selectedUsers,
        role: inviteRole,
        labels: labelsToSend,
      });
      toast.success(`${selectedUsers.length} membro${selectedUsers.length !== 1 ? 's' : ''} convidado${selectedUsers.length !== 1 ? 's' : ''}!`);
      setSelectedUsers([]);
      setInviteRole('member');
      setIdentifyGroups(null);
      setLabels({});
      setShowInviteModal(false);
      fetchBoardMembers();
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao convidar membros');
    } finally { setLoading(false); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remover este membro do quadro?')) return;
    try {
      await api.delete(`/projects/boards/${board.id}/members/${userId}`);
      toast.success('Membro removido');
      fetchBoardMembers();
      onDataChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao remover membro'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (newRole === 'owner') return;
    setChangingRole(prev => ({ ...prev, [userId]: true }));
    try {
      await api.put(`/projects/boards/${board.id}/members/${userId}`, { role: newRole });
      toast.success('Permissão atualizada!');
      fetchBoardMembers();
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar permissão');
    } finally { setChangingRole(prev => ({ ...prev, [userId]: false })); }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const availableUsers = allUsers.filter(u =>
    !boardMembers.some(m => m.user_id === u.id) &&
    (searchQuery === '' || u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Membros do Quadro</h3>
          <p className="text-sm text-gray-500 mt-1">{boardMembers.length} membro{boardMembers.length !== 1 ? 's' : ''} com acesso</p>
        </div>
        {canManageMembers && (
          <Button onClick={() => setShowInviteModal(true)} className="bg-blue-600 hover:bg-blue-700">
            👥 Convidar Membros
          </Button>
        )}
      </div>

      {/* Legenda de permissões */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ROLES.map(r => (
          <div key={r.value} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.color}`}>{r.label}</span>
            <p className="text-xs text-gray-500 mt-1.5">{r.description}</p>
          </div>
        ))}
      </div>

      {/* Lista de membros */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700">Membros Atuais</h4>
        </div>
        <div className="divide-y divide-gray-100">
          {boardMembers.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            const isOwner  = member.role === 'owner';
            const isSelf   = member.user_id === user?.id; // ninguém altera a própria permissão
            return (
              <div key={member.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <UserAvatar name={member.name} id={member.user_id} size="lg" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {member.name}
                      {member.display_label && <span className="ml-1.5 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700">{member.display_label}</span>}
                    </p>
                    <p className="text-xs text-gray-400">{member.email}</p>
                    {canManageMembers && editingLabel?.userId === member.user_id ? (
                      <div className="mt-1 flex items-center gap-1">
                        <input
                          autoFocus
                          value={editingLabel.value}
                          onChange={e => setEditingLabel({ userId: member.user_id, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') saveLabel(); if (e.key === 'Escape') setEditingLabel(null); }}
                          placeholder="Ex.: Pedro Henrique (Financeiro)"
                          className="w-56 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <button onClick={saveLabel} className="rounded bg-blue-600 px-2 py-1 text-xs text-white">Salvar</button>
                        <button onClick={() => setEditingLabel(null)} className="px-1 text-xs text-gray-400">Cancelar</button>
                      </div>
                    ) : canManageMembers && (
                      <button
                        onClick={() => setEditingLabel({ userId: member.user_id, value: member.display_label || '' })}
                        className="mt-0.5 text-xs text-blue-500 hover:underline"
                      >
                        {member.display_label ? 'Editar identificação' : 'Identificar'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {isOwner || isSelf || !canManageMembers ? (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${roleInfo.color}`}>{roleInfo.label}</span>
                  ) : (
                    <select
                      value={member.role}
                      disabled={changingRole[member.user_id]}
                      onChange={e => handleRoleChange(member.user_id, e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                    >
                      {ROLES.filter(r => r.value !== 'owner').map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  )}
                  {!isOwner && canManageMembers && (
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                    >
                      Remover
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {boardMembers.length === 0 && (
            <div className="py-10 text-center text-gray-400">
              <div className="text-3xl mb-2">👥</div>
              <p className="text-sm">Nenhum membro adicionado ainda</p>
              <p className="text-xs mt-1">Clique em "Convidar Membros" para começar</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de convite */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convidar Membros para o Quadro</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Busca */}
            <div>
              <Label>Buscar usuários</Label>
              <Input
                placeholder="Nome ou e-mail..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Selecionados */}
            {selectedUsers.length > 0 && (
              <div>
                <Label>Selecionados ({selectedUsers.length})</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedUsers.map(uid => {
                    const u = allUsers.find(x => x.id === uid);
                    return (
                      <Badge key={uid} className="bg-blue-100 text-blue-800 border-0 gap-1">
                        {u?.name || 'Usuário'}
                        <button onClick={() => toggleUserSelection(uid)} className="hover:text-red-600 ml-1">×</button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lista de usuários */}
            <div>
              <Label>Usuários disponíveis</Label>
              <div className="mt-2 max-h-52 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {availableUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => toggleUserSelection(u.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${selectedUsers.includes(u.id) ? 'bg-blue-50' : ''}`}
                  >
                    <UserAvatar name={u.name} id={u.id} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.email}</p>
                    </div>
                    {selectedUsers.includes(u.id) && (
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </div>
                ))}
                {availableUsers.length === 0 && (
                  <div className="py-6 text-center text-gray-400 text-sm">
                    {searchQuery ? 'Nenhum usuário encontrado' : 'Todos os usuários já são membros'}
                  </div>
                )}
              </div>
            </div>

            {/* Seleção de permissão */}
            <div>
              <Label>Permissão dos convidados</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {ROLES.filter(r => r.value !== 'owner').map(r => (
                  <button
                    key={r.value}
                    onClick={() => setInviteRole(r.value)}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${inviteRole === r.value ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-300'}`}
                  >
                    <p className={`text-xs font-semibold ${inviteRole === r.value ? 'text-blue-700' : 'text-gray-700'}`}>{r.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-tight">{r.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInviteModal(false); setSelectedUsers([]); setSearchQuery(''); }}>
              Cancelar
            </Button>
            <Button
              onClick={startInvite}
              disabled={selectedUsers.length === 0 || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Convidando…' : `Convidar ${selectedUsers.length > 0 ? selectedUsers.length : ''} membro${selectedUsers.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Identificação de homônimos */}
      <Dialog open={Boolean(identifyGroups)} onOpenChange={open => { if (!open) setIdentifyGroups(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Há pessoas com o mesmo nome</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-gray-600">
              Para não confundir os responsáveis nas tarefas, dê uma identificação diferente a cada pessoa
              (ex.: <em>Pedro Henrique (Financeiro)</em>).
            </p>
            {(identifyGroups || []).map((group, gi) => (
              <div key={gi} className="space-y-2 rounded-xl border border-gray-200 p-3">
                {group.map(p => {
                  const email = (p.current ? boardMembers.find(m => m.user_id === p.id)?.email : allUsers.find(u => u.id === p.id)?.email) || '';
                  return (
                    <div key={p.id} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {p.name} {p.current && <span className="text-xs font-normal text-gray-400">(já no quadro)</span>}
                        </p>
                        <p className="truncate text-xs text-gray-400">{email}</p>
                      </div>
                      <Input
                        value={labels[p.id] ?? ''}
                        onChange={e => setLabels(prev => ({ ...prev, [p.id]: e.target.value }))}
                        placeholder="Identificação"
                        className="w-52"
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIdentifyGroups(null)}>Voltar</Button>
            <Button onClick={confirmIdentification} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Convidando…' : 'Confirmar e convidar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
